"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Check } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { usePrefs } from "@/components/prefs-provider";
import { PLAN_PRICES, type PlanName } from "@/lib/plans";

type UserMe = {
  id: string;
  plan: { planName: string | null; active: boolean; hasUsedTrial: boolean } | null;
} | null;

export default function PlansPage() {
  const router = useRouter();
  const { t, tf } = usePrefs();
  const [user, setUser] = useState<UserMe>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [checkout, setCheckout] = useState<Exclude<PlanName, "Trial"> | null>(null);
  const [autoRenew, setAutoRenew] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((d) => setUser(d.user))
      .catch(() => setUser(null));
  }, []);

  async function startTrial() {
    if (!user) {
      router.push("/register");
      return;
    }
    setBusy("trial");
    setError("");
    const res = await fetch("/api/payments/trial", { method: "POST" });
    const data = await res.json();
    setBusy("");
    if (!res.ok) {
      setError(data.error || t.plans.trialFail);
      return;
    }
    router.push("/dashboard");
  }

  async function pay(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!checkout) return;
    if (!user) {
      router.push("/register");
      return;
    }
    setBusy("pay");
    setError("");
    const fd = new FormData(e.currentTarget);
    const res = await fetch("/api/payments/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        planName: checkout,
        autoRenew,
        cardNumber: fd.get("cardNumber"),
        cardName: fd.get("cardName"),
        expiry: fd.get("expiry"),
        cvc: fd.get("cvc"),
      }),
    });
    const data = await res.json();
    setBusy("");
    if (!res.ok) {
      setError(data.error || t.plans.payFail);
      return;
    }
    router.push("/dashboard");
  }

  const copy = {
    Trial: t.plans.trial,
    Basic: t.plans.basic,
    Family: t.plans.family,
    Pro: t.plans.pro,
  } as const;

  const plans: PlanName[] = ["Trial", "Basic", "Family", "Pro"];

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <SiteHeader solid />
      <main className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--teal)]">{t.plans.kicker}</p>
          <h1 className="font-display mt-3 text-4xl font-bold">{t.plans.h1}</h1>
          <p className="mt-3 text-[var(--ink-soft)]">{t.plans.lead}</p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((name) => {
            const p = copy[name];
            const current = user?.plan?.active && user.plan.planName === name;
            return (
              <div
                key={name}
                className={`flex flex-col rounded-2xl border p-5 ${
                  name === "Family"
                    ? "border-[var(--teal)] bg-[var(--foam)] shadow-[var(--shadow)]"
                    : "border-[var(--line)] bg-[var(--foam)]/70"
                }`}
              >
                <p className="font-display text-xl font-bold">{p.label}</p>
                <p className="mt-2 text-2xl font-bold text-[var(--teal)]">{p.price}</p>
                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                  {tf(t.plans.slots, { n: name === "Trial" || name === "Basic" ? 1 : name === "Family" ? 3 : 8 })}
                </p>
                <ul className="mt-5 flex-1 space-y-2">
                  {p.f.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-[var(--ink-soft)]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--teal)]" />
                      {f}
                    </li>
                  ))}
                </ul>
                {name === "Trial" ? (
                  <button
                    disabled={!!busy || !!user?.plan?.hasUsedTrial}
                    onClick={startTrial}
                    className="btn-primary mt-6 w-full !py-2.5 text-sm"
                  >
                    {user?.plan?.hasUsedTrial ? t.plans.trialUsed : busy === "trial" ? t.plans.starting : t.plans.startTrial}
                  </button>
                ) : (
                  <button
                    disabled={!!busy || current}
                    onClick={() => {
                      if (!user) router.push("/register");
                      else setCheckout(name);
                    }}
                    className="btn-primary mt-6 w-full !py-2.5 text-sm"
                  >
                    {current ? t.plans.current : tf(t.plans.choose, { name })}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {error && <p className="mt-6 text-[var(--alert)]">{error}</p>}

        {checkout && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 sm:items-center">
            <div className="w-full max-w-md rounded-2xl bg-[var(--foam)] p-6 shadow-xl">
              <h2 className="font-display text-2xl font-bold">{t.plans.checkout}</h2>
              <p className="mt-1 text-sm text-[var(--ink-soft)]">
                {checkout} · ₪{PLAN_PRICES[checkout]} · {t.plans.demo}
              </p>
              <form onSubmit={pay} className="mt-5 space-y-3">
                <input name="cardName" required placeholder={t.plans.cardName} className="input" />
                <input name="cardNumber" required placeholder={t.plans.cardNumber} className="input" />
                <div className="grid grid-cols-2 gap-3">
                  <input name="expiry" required placeholder="MM/YY" className="input" />
                  <input name="cvc" required placeholder="CVC" className="input" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
                  {t.plans.autoRenew}
                </label>
                <div className="flex gap-2 pt-2">
                  <button type="button" className="btn-ghost flex-1 !py-2.5" onClick={() => setCheckout(null)}>
                    {t.plans.cancel}
                  </button>
                  <button disabled={busy === "pay"} className="btn-primary flex-1 !py-2.5" type="submit">
                    {busy === "pay" ? t.plans.charging : t.plans.payNow}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <p className="mt-10 text-sm text-[var(--ink-soft)]">
          {t.plans.already}{" "}
          <Link className="font-semibold text-[var(--teal)]" href="/dashboard">
            {t.plans.goDash}
          </Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
